import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, EyeOff, Loader2, Lock, User } from 'lucide-react';
import { motion } from 'framer-motion';

// Floating 3D Glass Shape Component
const FloatingShape = ({ className, delay = 0, duration = 20 }) => (
  <motion.div
    className={`absolute rounded-3xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 ${className}`}
    animate={{
      y: [0, -30, 0],
      rotate: [0, 5, -5, 0],
      scale: [1, 1.05, 1],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

// Glowing Orb Component
const GlowingOrb = ({ className, color }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl ${className}`}
    style={{ background: color }}
    animate={{
      opacity: [0.3, 0.6, 0.3],
      scale: [1, 1.2, 1],
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#0F172A] to-[#020617]" />
      
      {/* Glowing Orbs */}
      <GlowingOrb 
        className="w-[600px] h-[600px] -top-48 -left-48" 
        color="radial-gradient(circle, rgba(249,115,22,0.15) 0%, transparent 70%)" 
      />
      <GlowingOrb 
        className="w-[500px] h-[500px] -bottom-32 -right-32" 
        color="radial-gradient(circle, rgba(234,179,8,0.12) 0%, transparent 70%)" 
      />
      <GlowingOrb 
        className="w-[400px] h-[400px] top-1/2 left-1/4" 
        color="radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%)" 
      />

      {/* Floating 3D Glass Shapes */}
      <FloatingShape className="w-32 h-32 top-20 left-[15%]" delay={0} duration={18} />
      <FloatingShape className="w-24 h-24 top-40 right-[20%]" delay={2} duration={22} />
      <FloatingShape className="w-40 h-40 bottom-32 left-[10%]" delay={1} duration={20} />
      <FloatingShape className="w-20 h-20 bottom-20 right-[15%]" delay={3} duration={16} />
      <FloatingShape className="w-28 h-28 top-1/3 right-[10%]" delay={1.5} duration={24} />
      <FloatingShape className="w-16 h-16 top-1/4 left-[30%]" delay={2.5} duration={19} />

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="bg-slate-900/60 backdrop-blur-2xl border border-white/10 shadow-2xl shadow-black/50 rounded-3xl overflow-hidden">
          {/* Top Glow Line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
          
          {/* Card Content */}
          <CardContent className="p-8 pt-10">
            {/* Logo */}
            <motion.div 
              className="flex justify-center mb-8"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-orange-500/30 rounded-2xl blur-xl" />
                {/* Logo Container */}
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shadow-lg shadow-orange-500/30 border border-orange-400/30">
                  <span className="text-4xl font-black text-white tracking-tighter">T</span>
                </div>
              </div>
            </motion.div>
            
            {/* Title */}
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-2xl font-bold text-white tracking-wide mb-2">
                TADABER WORKFORCE
              </h1>
              <p className="text-slate-400 text-sm">
                Domestic Workforce Management System
              </p>
            </motion.div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error message */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center backdrop-blur-sm"
                >
                  {error}
                </motion.div>
              )}

              {/* Username */}
              <motion.div 
                className="space-y-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Label htmlFor="username" className="text-slate-300 text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-orange-500" />
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="h-12 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-orange-500 focus:ring-orange-500/20 rounded-xl transition-all"
                  required
                  data-testid="login-username-input"
                />
              </motion.div>

              {/* Password */}
              <motion.div 
                className="space-y-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Label htmlFor="password" className="text-slate-300 text-sm font-medium flex items-center gap-2">
                  <Lock className="h-4 w-4 text-orange-500" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-500 focus:border-orange-500 focus:ring-orange-500/20 rounded-xl pr-12 transition-all"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-orange-500 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </motion.div>

              {/* Submit button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base tracking-wide rounded-xl shadow-lg shadow-orange-500/25 transition-all duration-300 hover:shadow-orange-500/40 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Footer */}
            <motion.div 
              className="mt-8 pt-6 border-t border-white/5 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <p className="text-xs text-slate-500">
                v3.7 - Tadaber Workforce Management System
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#020617] to-transparent pointer-events-none" />
    </div>
  );
};

export default LoginPage;
